import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Search, Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getImageUser } from "../../global-components/src/utils/auth";
import { Chat, MessageChat } from "../../types";
import "../../components/NewConversationPanel/style.css";
import "./forward-message-modal.css";

interface Props {
    show: boolean;
    messages: MessageChat[];
    selectedIds: number[];
    members: Chat[] | null;
    onClose: () => void;
    onForward: (targetChatId: number) => void;
}

const ForwardMessageModal = ({ show, messages, selectedIds, members, onClose, onForward }: Props) => {
    const { t } = useTranslation();
    const [rendered, setRendered] = useState(show);
    const [entering, setEntering] = useState(false);
    const [search, setSearch] = useState("");
    const [sending, setSending] = useState<number | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (show) {
            setRendered(true);
            const raf = requestAnimationFrame(() => {
                setEntering(true);
                setTimeout(() => inputRef.current?.focus(), 80);
            });
            return () => cancelAnimationFrame(raf);
        } else {
            setEntering(false);
            const timer = setTimeout(() => {
                setRendered(false);
                setSearch("");
                setSending(null);
            }, 220);
            return () => clearTimeout(timer);
        }
    }, [show]);

    if (!rendered) return null;

    const term = search.trim().toLowerCase();
    const chats = (members ?? []).filter(c => {
        if (!term) return true;
        const label = (c.is_group ? (c.group_name ?? "") : (c.nickname ?? c.name ?? "")).toLowerCase();
        const sub = (c.is_group ? "" : (c.user_name ?? "")).toLowerCase();
        return label.includes(term) || sub.includes(term);
    });

    const previewText = (() => {
        const msg = messages.find(m => m.id === selectedIds[0]);
        if (!msg) return "";
        if (msg.text?.trim()) return msg.text.slice(0, 60);
        if (msg.gif_url) return "GIF";
        if (msg.file_path) {
            try {
                const files = JSON.parse(msg.file_path);
                if (files.length) return `📎 ${files.length > 1 ? files.length + " arquivos" : "Arquivo"}`;
            } catch { /* empty */ }
        }
        return "";
    })();

    const handlePick = async (chatId: number) => {
        if (sending !== null) return;
        setSending(chatId);
        await onForward(chatId);
        onClose();
    };

    return (
        <>
            <div className={`ncp-backdrop${entering ? " ncp-in" : ""}`} onClick={onClose} />
            <div className={`ncp-panel fwd-panel${entering ? " ncp-in" : ""}`}>
                <div className="ncp-header">
                    <button className="ncp-back-btn" onClick={onClose} aria-label={t("generics.back")}>
                        <ArrowLeft size={20} />
                    </button>
                    <h2 className="ncp-title">{t("chat.forwardTo")}</h2>
                </div>

                {selectedIds.length === 1 && previewText && (
                    <div className="fwd-preview">{previewText}</div>
                )}
                {selectedIds.length > 1 && (
                    <div className="fwd-preview">{selectedIds.length} mensagens</div>
                )}

                <div className="ncp-search-wrap">
                    <Search size={15} className="ncp-search-icon" />
                    <input
                        ref={inputRef}
                        className="ncp-search-input"
                        placeholder={t("generics.search")}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>

                <div className="ncp-results">
                    {chats.length === 0 && (
                        <p className="ncp-empty">{t("generics.noResults")}</p>
                    )}
                    {chats.map(c => {
                        const label = c.is_group ? (c.group_name ?? t("chat.group.panelTitle")) : (c.nickname ?? c.name);
                        const sub = c.is_group ? null : (c.user_name ? `@${c.user_name}` : null);
                        const img = c.is_group ? (c.group_img ? getImageUser(c.group_img) : null) : getImageUser(c.img_account);
                        const isSending = sending === c.chat_id;
                        return (
                            <div key={c.chat_id} className="ncp-user-row" onClick={() => handlePick(c.chat_id)}>
                                {img
                                    ? <img src={img} className="ncp-user-img" onError={e => { e.currentTarget.src = "/img/img-account.png"; }} alt="" />
                                    : <div className="ncp-user-img" style={{ background: "var(--hover-background,#e0e0e0)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>👥</div>
                                }
                                <div className="ncp-user-info">
                                    <span className="ncp-user-name">{label}</span>
                                    {sub && <span className="ncp-user-sub">{sub}</span>}
                                </div>
                                <div className="fwd-send-icon" style={{ opacity: isSending ? 1 : 0.35 }}>
                                    <Send size={18} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </>
    );
};

export default ForwardMessageModal;
